import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { getDeviceInfo } from "../utils/getDeviceInfo";
import { handleError, handleWarning } from '../utils/errorHandling';
import { handleChunkDataAvailable } from '../utils/session/handleChunkDataAvailable';
import startSession from '../utils/session/startSession';
import { useTranslation } from 'react-i18next';
import useWakeLock from './useWakeLock';
import useDetectSilence from './useDetectSilence';
import supportedBrowser from '../utils/supportedBrowser';
import { Bugsnag } from "../bugsnag";
import { logEvent } from '../analytics';
import useAudioDevices from './useAudioDevices';

const toErrorMessage = (err) => String(err?.message ?? err);

// _initialChunksCount should be used only in tests, to simulate the number of chunks that have been recorded
export const useRecorder = (_initialChunksCount = 0) => {
  // Each audio partial is 30 seconds long
  const PARTIAL_SIZE_IN_SEC = 30;
  const PARTIAL_SIZE_IN_MSEC = PARTIAL_SIZE_IN_SEC * 1000;
  const { t } = useTranslation();

  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [sessionUuid, setSessionUuid] = useState(null);
  const [recorder, setRecorder] = useState(null);
  const [totalChunks, setTotalChunks] = useState(_initialChunksCount);
  const {
    audioDevices,
    selectedAudioDeviceId,
    setSelectedAudioDeviceId,
    refreshDevices,
    pickFallbackDeviceId,
  } = useAudioDevices();
  const activeAudioTrackRef = useRef(null);
  const endedHandlerRef = useRef(null);
  const recorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const inputStreamRef = useRef(null);
  // Monotonic, in-memory counter for the next chunk index to assign.
  // Prevents duplicate indexes when multiple dataavailable events overlap.
  const nextChunkIndexRef = useRef(_initialChunksCount);
  const isEndingRef = useRef(false);
  const wakeLockStoppedRef = useRef(false);
  // Reserve the next chunk index and advance counters (DRY for ondataavailable)
  const reserveNextChunkIndex = useCallback(() => {
    const assignedIndex = nextChunkIndexRef.current;
    nextChunkIndexRef.current = assignedIndex + 1;
    return assignedIndex;
  }, []);
  // Keep a separate, live ref of the "paused intent" across async boundaries.
  // MediaRecorder.state can be null/stale while streams are torn down/recreated
  // (e.g., after devicechange/track ended). We use this ref to decide whether
  // the newly created MediaRecorder should be paused immediately to preserve
  // the UI state, regardless of transient recorder.state values.
  const isPausedRef = useRef(false);

  // Silence detection
  const [analyser, setAnalyser] = useState(null);
  const { problemType, problemStats } = useDetectSilence(analyser, isRecording, isPaused);

  const deviceInfo = getDeviceInfo();
  const isSupportedBrowser = supportedBrowser();

  const { startWakeLock, stopWakeLock } = useWakeLock();

  const isSupportedMediaRecorder = () => {
    return navigator.mediaDevices && navigator.mediaDevices.getUserMedia && typeof MediaRecorder !== 'undefined';
  };

  const getAudioMimeType = () => {
    const supportedAudioFormats = require('../utils/supportedAudioFormats').default;
    return supportedAudioFormats.find((type) => MediaRecorder.isTypeSupported(type));
  };

  // Keep a live ref of pause state for async handlers
  // Avoid stale-closure bugs in async event handlers by syncing the ref here.
  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  // Internal helpers to manage the audio track 'ended' listener
  const detachAudioTrackEndedListener = useCallback(() => {
    if (
      activeAudioTrackRef.current &&
      endedHandlerRef.current &&
      typeof activeAudioTrackRef.current.removeEventListener === 'function'
    ) {
      activeAudioTrackRef.current.removeEventListener('ended', endedHandlerRef.current);
    }
  }, []);

  // Reflect the actual device id being used by the stream into our selection state
  const updateSelectedDeviceFromStream = useCallback((stream) => {
    try {
      const track = typeof stream.getAudioTracks === 'function' ? stream.getAudioTracks()[0] : null;
      const settings = track && typeof track.getSettings === 'function' ? track.getSettings() : null;
      const actualDeviceId = settings && settings.deviceId ? settings.deviceId : null;
      if (actualDeviceId) {
        setSelectedAudioDeviceId(actualDeviceId);
      }
    } catch (_) {}
  }, [setSelectedAudioDeviceId]);

  // Build getUserMedia constraints for an optional device id
  const buildAudioConstraints = useCallback((deviceId) => ({
    audio: {
      deviceId: deviceId && deviceId !== 'default' ? { exact: deviceId } : undefined,
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
      channelCount: 1,
    }
  }), []);

  // Try preferred device; on Overconstrained/NotFound fallback to browser default or a safe device
  const getAudioStreamWithFallback = useCallback(async (preferredDeviceId) => {
    try {
      return await navigator.mediaDevices.getUserMedia(buildAudioConstraints(preferredDeviceId));
    } catch (errPrimary) {
      if (errPrimary?.name === 'OverconstrainedError' || errPrimary?.name === 'NotFoundError') {
        console.warn('[useRecorder] getUserMedia failed with selected device. Trying fallback...', errPrimary);
        try {
          const fallbackId = await pickFallbackDeviceId();
          const effectiveId = fallbackId === 'default' ? null : fallbackId;
          const stream = await navigator.mediaDevices.getUserMedia(buildAudioConstraints(effectiveId));
          if (fallbackId && fallbackId !== 'default') {
            setSelectedAudioDeviceId(fallbackId);
          }
          return stream;
        } catch (errFallback) {
          console.error('[useRecorder] Fallback getUserMedia also failed', errFallback);
          throw errPrimary;
        }
      }
      throw errPrimary;
    }
  }, [buildAudioConstraints, pickFallbackDeviceId, setSelectedAudioDeviceId]);

  // Close AudioContext and clear analyser
  const disposeAudioContext = useCallback(async () => {
    try {
      if (audioContextRef.current && typeof audioContextRef.current.close === 'function') {
        await audioContextRef.current.close();
      }
    } catch (_) {}
    audioContextRef.current = null;
    setAnalyser(null);
  }, []);

  const stopStreamTracks = useCallback((stream) => {
    try {
      if (stream && typeof stream.getTracks === 'function') {
        stream.getTracks().forEach((t) => {
          try { t.stop(); } catch (_) {}
        });
      }
    } catch (_) {}
  }, []);

  const setupAnalyser = (inputStream) => {
    const audioContext = new (window.AudioContext)();
    audioContextRef.current = audioContext;
    const audioSource = audioContext.createMediaStreamSource(inputStream);
    const analyserNode = audioContext.createAnalyser();
    audioSource.connect(analyserNode);
    setAnalyser(analyserNode);
  };

  // Helper to stop the current MediaRecorder and dispose references.
  const stopAndDisposeRecorder = useCallback(async () => {
    const currentRecorder = recorderRef.current || recorder;
    if (!currentRecorder) return;
    // Detach listener first to avoid handling 'ended' during teardown
    detachAudioTrackEndedListener();
    // Flush any buffered data before stopping to avoid losing partial chunks
    try { currentRecorder.requestData && currentRecorder.requestData(); } catch (_) {}
    if (currentRecorder.state !== 'inactive') {
      currentRecorder.stop();
    }
    setRecorder(null);
    recorderRef.current = null;
    // Tracks will be stopped in onstop handler; avoid duplicate work here
  }, [detachAudioTrackEndedListener, recorder, setRecorder]);

  // moved below switchToDevice to avoid TDZ

  // Helper function to reset recorder states
  const resetRecordingState = () => {
    setRecorder(null);
    setIsRecording(false);
    setIsPaused(false);
    setSessionUuid(null);
  };

  // Device enumeration and change listeners are handled by useAudioDevices

  // pauseImmediately: if true, start and immediately pause the new
  // MediaRecorder instance to preserve the paused UI state after a device
  // switch or a track-ended recovery.
  const requestAudioPermission = useCallback(async (uuid, deviceIdOverride = null, options = {}) => {
    try {
      const deviceToUse = deviceIdOverride || selectedAudioDeviceId;
      const audioMimeType = getAudioMimeType();
      // Acquire input stream from the physical device
      const inputStream = await getAudioStreamWithFallback(deviceToUse);
      inputStreamRef.current = inputStream;
      const pauseImmediately = options && options.pauseImmediately === true;

      // After permission is granted, enumerate devices again to get labels
      await refreshDevices();

      // Listen for current device track ending (e.g., device unplugged) and auto-fallback
      attachAudioTrackEndedListener(inputStream);

      const recorderOptions = audioMimeType ? { mimeType: audioMimeType } : undefined;
      console.log(`MediaRecorder: using ${audioMimeType || 'default'} format for audio.`);
      const mediaRecorder = new MediaRecorder(inputStream, recorderOptions);
      // Continue chunk numbering across device switches using the in-memory counter

      // context, source and analyser needed for silence detection
      setupAnalyser(inputStream);

      mediaRecorder.ondataavailable = async (e) => {
        const size = e && e.data && typeof e.data.size === 'number' ? e.data.size : 0;
        if (!e || size === 0) {
          console.log(`[recorder] ondataavailable ignored empty blob; ending=${isEndingRef.current}`);
          return;
        }
        // Reserve an index synchronously to avoid duplicates on overlapping events
        const assignedChunkIndex = reserveNextChunkIndex();
        const nextTotal = assignedChunkIndex + 1;
        setTotalChunks(nextTotal);
        console.log(`[recorder] ondataavailable start idx=${assignedChunkIndex} size=${size} ending=${isEndingRef.current} total->${nextTotal} mime=${audioMimeType}`);
        await handleChunkDataAvailable(e, {
          audioMimeType,
          sessionUuid: uuid,
          currentChunkIndex: assignedChunkIndex,
          setTotalChunks,
        });
        console.log(`[recorder] ondataavailable uploaded idx=${assignedChunkIndex}`);
      };

      mediaRecorder.onstop = async () => {
        console.log('MediaRecorder: stopped');
        stopStreamTracks(mediaRecorder.stream);
        stopStreamTracks(inputStreamRef.current);
        inputStreamRef.current = null;
        // Close the AudioContext to release audio resources
        await disposeAudioContext();
        if (!wakeLockStoppedRef.current) {
          await stopWakeLock();
          wakeLockStoppedRef.current = true;
        }
      };

      mediaRecorder.onpause = () => {
        console.log('MediaRecorder: paused');
        // Ensure any remaining data is flushed
        mediaRecorder.requestData();
      };

      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event.error);
        Bugsnag.notify(new Error('mediaRecorder.onerror: ' + toErrorMessage(event?.error)));
        handleError(t('session_recorder.errors.access_device'));
        resetRecordingState();
        throw new Error(t('session_recorder.errors.access_device'));
      };

      setRecorder(mediaRecorder);
      recorderRef.current = mediaRecorder;
      // Reflect the actual device id and capture settings for debug
      try {
        const audioTrack = typeof inputStream.getAudioTracks === 'function' ? inputStream.getAudioTracks()[0] : null;
        const settings = audioTrack && typeof audioTrack.getSettings === 'function' ? audioTrack.getSettings() : null;
        console.log('[recorder] track.getSettings():', settings);
      } catch (_) {}
      updateSelectedDeviceFromStream(inputStream);
      await startWakeLock();
      wakeLockStoppedRef.current = false;
      console.log(`MediaRecorder: starting recording in chunks of ${PARTIAL_SIZE_IN_SEC} seconds`);
      mediaRecorder.start(PARTIAL_SIZE_IN_MSEC);
      if (pauseImmediately) {
        if (mediaRecorder.state === 'recording') {
          mediaRecorder.pause();
        }
        setIsRecording(false);
        setIsPaused(true);
      } else {
        setIsRecording(true);
        setIsPaused(false);
      }
    } catch (err) {
      Bugsnag.leaveBreadcrumb('requestAudioPermission error: ' + toErrorMessage(err))
      handleError(t('session_recorder.errors.access_device'));
      resetRecordingState();
      // Propagate the error for the UI to handle
      throw err;
    }
  }, [
    selectedAudioDeviceId,
    getAudioStreamWithFallback,
    refreshDevices,
    updateSelectedDeviceFromStream,
    startWakeLock,
    stopWakeLock,
    t,
  ]);

  // Initial population is handled by useAudioDevices

  // End recording and cleanup
  const end = async () => {
    if (recorderRef.current || recorder) {
      console.log('[recorder] end() called');
      isEndingRef.current = true;
      await stopAndDisposeRecorder();
      // The onstop handler will also stop tracks and stopWakeLock; do not duplicate
      if (!wakeLockStoppedRef.current) {
        await stopWakeLock();
        wakeLockStoppedRef.current = true;
      }
      setIsRecording(false);
      // Close the AudioContext if still open (defensive; onstop should have done it)
      await disposeAudioContext();
      // Reset total chunks count on full stop
      setTotalChunks(0);
      nextChunkIndexRef.current = 0;
      // Force reinitialization on next start
      resetRecordingState();
      isEndingRef.current = false;
    } else {
      // Ensure listener is detached even if recorder is already missing
      detachAudioTrackEndedListener();
    }
  };

  const switchToDevice = useCallback(async (deviceId, { force = false, pauseImmediately = null } = {}) => {
    // Avoid redundant re-acquire unless forced or special-casing 'default'
    if (!force && selectedAudioDeviceId === deviceId) {
      if (deviceId !== 'default') return;
    }

    const wasRecording = isRecording;
    const wasPaused = isPaused;

    try {
      await stopAndDisposeRecorder();
      setSelectedAudioDeviceId(deviceId);

      if (force || wasRecording || wasPaused) {
        await requestAudioPermission(
          sessionUuid,
          deviceId === 'default' ? null : deviceId,
          { pauseImmediately: (pauseImmediately === true || wasPaused === true) }
        );
      }
    } catch (error) {
      console.error("Failed to switch microphone", error);
      handleError(t('session_recorder.errors.access_device'));
      // If switching fails, end the session to avoid an inconsistent state.
      end();
    }
  }, [
    selectedAudioDeviceId,
    isRecording,
    isPaused,
    stopAndDisposeRecorder,
    setSelectedAudioDeviceId,
    requestAudioPermission,
    sessionUuid,
    end,
  ]);

  const attachAudioTrackEndedListener = useCallback(
    (stream) => {
      const audioTracks = typeof stream.getAudioTracks === 'function' ? stream.getAudioTracks() : [];
      const currentAudioTrack = audioTracks && audioTracks.length > 0 ? audioTracks[0] : null;

      // Always detach any previous listener before attaching a new one
      detachAudioTrackEndedListener();

      if (currentAudioTrack && typeof currentAudioTrack.addEventListener === 'function') {
        activeAudioTrackRef.current = currentAudioTrack;
        const handleTrackEnded = async (event) => {
          // Ignore events from stale tracks
          if (
            event &&
            event.target &&
            activeAudioTrackRef.current &&
            event.target !== activeAudioTrackRef.current
          ) {
            return;
          }
          try {
            const fallbackDeviceId = await pickFallbackDeviceId();
            if (fallbackDeviceId) {
              // Re-acquire through the single switching function to keep logic centralized
              await switchToDevice(fallbackDeviceId, { force: true, pauseImmediately: isPausedRef.current === true });
            } else {
              handleError(t('session_recorder.errors.access_device'));
              await end();
            }
          } catch (e) {
            Bugsnag.notify(e);
          }
        };
        endedHandlerRef.current = handleTrackEnded;
        currentAudioTrack.addEventListener('ended', handleTrackEnded);
      }
    },
    [detachAudioTrackEndedListener, pickFallbackDeviceId, switchToDevice, end]
  );

  const changeAudioDevice = useCallback(async (deviceId, { force = false } = {}) => {
    return switchToDevice(deviceId, { force });
  }, [switchToDevice]);


  // Explicit start method
  const start = useCallback(async (providedSessionUuid = null) => {
    if (!isSupportedBrowser || !isSupportedMediaRecorder()) {
      handleWarning(t('session_recorder.errors.unsupported_browser'));
      Bugsnag.notify(new Error(`Unsupported Browser: ${JSON.stringify(deviceInfo)}`));
      throw new Error('Unsupported browser');
    }

    if (!recorder) {
      let sessionToUse = providedSessionUuid;
      if (!sessionToUse) {
        sessionToUse = await startSession();
        if (!sessionToUse) {
          handleError(t('session_recorder.errors.start'));
          throw new Error(t('session_recorder.errors.start'));
        }
      }
      setSessionUuid(sessionToUse);
      await requestAudioPermission(sessionToUse); // May throw if mic access fails.
      logEvent('SessionRecorder', 'Start');
    }
  }, [recorder, requestAudioPermission]);
  // Cleanup on unmount to release resources if the host component forgets
  useEffect(() => () => { try { end(); } catch (_) {} }, []);

  // Explicit pause method
  const pause = useCallback(() => {
    if (recorder && recorder.state === 'recording') {
      recorder.pause();
      setIsRecording(false);
      setIsPaused(true);
    }
  }, [recorder]);

  // Explicit resume method
  const resume = useCallback(() => {
    if (recorder && recorder.state === 'paused') {
      recorder.resume();
      setIsRecording(true);
      setIsPaused(false);
      console.log('[useRecorder] Resumed recording');
    }
  }, [recorder]);



  return useMemo(() => ({
    isRecording,
    isPaused,
    pause,
    resume,
    start,
    end,
    sessionUuid,
    totalChunks,
    problemType,
    problemStats,
    audioDevices,
    selectedAudioDevice: selectedAudioDeviceId,
    changeAudioDevice,
  }), [
    isRecording,
    isPaused,
    pause,
    resume,
    start,
    end,
    sessionUuid,
    totalChunks,
    problemType,
    problemStats,
    audioDevices,
    selectedAudioDeviceId,
    changeAudioDevice,
  ]);
};
